import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/Base/Popover";
import {
  CardColor,
  Employee,
  IconNames,
  Tag,
  TerminologyOptions,
} from "@/types/v2";
import { Icon } from "@/components/Common/Icon";
import { getServiceAdvisorColor } from "@/lib/employees";
import { Badge } from "@/components/Base/Badge";
import { useState } from "react";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer";
import { MultiSelect } from "@/components/Base/MultiSelect";
import { Button } from "@/components/Base/Button";
import { DatePicker } from "@/components/Base/DatePicker";
import { addDays, endOfWeek, format, parse, startOfWeek } from "date-fns";
import { Tags } from "@/components/Common/Tags";
import { getUniqueValues } from "@/lib/utils";
import { getMatchingDateRange, parseDateString } from "@/lib/dateUtils";

type DashboardFiltersProps = {
  serviceAdvisors?: Employee[];
  technicians?: Employee[];
  serviceAdvisorId?: string | string[];
  onServiceWriterChange: (value: string[]) => void;
  technicianId?: string | string[];
  onTechnicianChange: (value: string[]) => void;
  isServiceAdvisorFilterEnabled?: boolean;
  isTechnicianFilterEnabled?: boolean;
  dueInStart?: string | null;
  onDueInStartChange?: (value: string | undefined) => void;
  dueInEnd?: string | null;
  onDueInEndChange?: (value: string | undefined) => void;
  dueOutStart?: string | null;
  onDueOutStartChange?: (value: string | undefined) => void;
  dueOutEnd?: string | null;
  onDueOutEndChange?: (value: string | undefined) => void;
  apptStartStart?: string | null;
  onApptStartStartChange?: (value: string | undefined) => void;
  apptStartEnd?: string | null;
  onApptStartEndChange?: (value: string | undefined) => void;
  apptEndStart?: string | null;
  onApptEndStartChange?: (value: string | undefined) => void;
  apptEndEnd?: string | null;
  onApptEndEndChange?: (value: string | undefined) => void;
  tags?: Tag[];
  filteringTags?: string[];
  onFilteringTagsChange?: (tags: string[]) => void;
  appliedFiltersCount?: number;
  colors?: CardColor[];
  filteringColors?: string[];
  onFilteringColorsChange?: (colors: string[]) => void;
  containers?: { id: string; displayName?: string }[];
  filteringContainer?: string[];
  onFilteringContainerChange?: (containerIds: string[]) => void;
};
type Range = "past" | "today" | "week" | "nextWeek";
export const DashboardFilters = ({
  serviceAdvisors,
  technicians,
  serviceAdvisorId,
  onServiceWriterChange,
  technicianId,
  onTechnicianChange,
  isServiceAdvisorFilterEnabled,
  isTechnicianFilterEnabled,
  dueInStart,
  dueInEnd,
  onDueInStartChange,
  onDueInEndChange,
  dueOutStart,
  dueOutEnd,
  onDueOutStartChange,
  onDueOutEndChange,
  apptStartStart,
  apptStartEnd,
  onApptStartStartChange,
  onApptStartEndChange,
  apptEndStart,
  apptEndEnd,
  onApptEndStartChange,
  onApptEndEndChange,
  tags,
  filteringTags,
  onFilteringTagsChange,
  appliedFiltersCount = 0,
  colors,
  filteringColors,
  onFilteringColorsChange,
  containers,
  filteringContainer,
  onFilteringContainerChange,
}: DashboardFiltersProps) => {
  const { terminolize } = useTerminologyReplacer();

  const [isOpen, setIsOpen] = useState(false);

  const handleClearAll = () => {
    onServiceWriterChange([]);
    onTechnicianChange([]);
    if (onDueInStartChange) {
      onDueInStartChange(undefined);
    }
    if (onDueInEndChange) {
      onDueInEndChange(undefined);
    }
    if (onDueOutStartChange) {
      onDueOutStartChange(undefined);
    }
    if (onDueOutEndChange) {
      onDueOutEndChange(undefined);
    }
    if (onApptStartStartChange) {
      onApptStartStartChange(undefined);
    }
    if (onApptStartEndChange) {
      onApptStartEndChange(undefined);
    }
    if (onApptEndStartChange) {
      onApptEndStartChange(undefined);
    }
    if (onApptEndEndChange) {
      onApptEndEndChange(undefined);
    }
    if (onFilteringTagsChange) {
      onFilteringTagsChange([]);
    }
    if (onFilteringColorsChange) {
      onFilteringColorsChange([]);
    }
    if (onFilteringContainerChange) {
      onFilteringContainerChange([]);
    }
  };

  const handleDueChange = (
    range: Range,
    onStartChange?: (value: string | undefined) => void,
    onEndChange?: (value: string | undefined) => void
  ) => {
    const now = new Date();
    if (range === "past") {
      if (onStartChange) {
        onStartChange(undefined);
      }
      if (onEndChange) {
        onEndChange(format(now, "yyyy-MM-dd"));
      }
    } else if (range === "today") {
      if (onStartChange) {
        onStartChange(format(now, "yyyy-MM-dd"));
      }
      if (onEndChange) {
        onEndChange(format(now, "yyyy-MM-dd"));
      }
    } else if (range === "week") {
      if (onStartChange) {
        onStartChange(format(startOfWeek(now), "yyyy-MM-dd"));
      }
      if (onEndChange) {
        onEndChange(format(endOfWeek(now), "yyyy-MM-dd"));
      }
    } else if (range === "nextWeek") {
      if (onStartChange) {
        onStartChange(format(startOfWeek(addDays(now, 7)), "yyyy-MM-dd"));
      }
      if (onEndChange) {
        onEndChange(format(endOfWeek(addDays(now, 7)), "yyyy-MM-dd"));
      }
    }
  };

  const handleDefaultDueClick = (
    type: "in" | "out" | "apptStart" | "apptEnd",
    range: Range
  ) => {
    if (type === "in") {
      handleDueChange(range, onDueInStartChange, onDueInEndChange);
    } else if (type === "out") {
      handleDueChange(range, onDueOutStartChange, onDueOutEndChange);
    } else if (type === "apptStart") {
      handleDueChange(range, onApptStartStartChange, onApptStartEndChange);
    } else if (type === "apptEnd") {
      handleDueChange(range, onApptEndStartChange, onApptEndEndChange);
    }
  };

  const handleTagAdd = (tag: string) => {
    if (onFilteringTagsChange) {
      onFilteringTagsChange(getUniqueValues([...(filteringTags ?? []), tag]));
    }
  };

  const handleTagRemove = (index: number) => {
    if (onFilteringTagsChange) {
      const tmpTags = [...(filteringTags ?? [])];
      onFilteringTagsChange(
        tmpTags.length === 1 ? [] : tmpTags.filter((_, i) => i !== index)
      );
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger className="relative flex h-9 items-center rounded-md p-2 text-sm font-semibold hover:bg-slate-200">
        {appliedFiltersCount > 0 ? (
          <Badge className={"absolute -left-1 -top-2"} type={"small"}>
            {appliedFiltersCount}
          </Badge>
        ) : null}
        <Icon name={IconNames.filters} className="mr-2" size={20} /> Filters
      </PopoverTrigger>
      <PopoverContent className="flex w-96 flex-col gap-2 p-2">
        <div className="flex h-14 flex-row items-center justify-between gap-2 border-b border-primary pb-2">
          <h3 className="text-lg font-bold text-primary">Filters</h3>
          {appliedFiltersCount > 0 && (
            <Button variant="secondary" onClick={handleClearAll}>
              <Icon name={IconNames.clear} size={16} className={"mr-2"} />
              Clear All ({appliedFiltersCount})
            </Button>
          )}
        </div>
        {isServiceAdvisorFilterEnabled && (
          <div className="grid grid-cols-2 items-center gap-1">
            <p className="text-sm text-primary">
              {terminolize(
                "Service Advisor",
                TerminologyOptions.serviceAdvisor,
                "singular"
              )}
            </p>
            <MultiSelect
              onChange={(value) => onServiceWriterChange(value)}
              value={
                typeof serviceAdvisorId === "string"
                  ? [serviceAdvisorId]
                  : (serviceAdvisorId ?? [])
              }
              placeholder={"All"}
              options={
                serviceAdvisors
                  ?.filter((sa) => !sa.hideInShopHero)
                  .map((serviceAdvisor) => ({
                    value: serviceAdvisor.id,
                    label: `${serviceAdvisor.firstName} ${serviceAdvisor.lastName}`,
                  })) ?? []
              }
              renderItem={(option) => {
                const serviceAdvisor = serviceAdvisors?.find(
                  (s) => s.id === option.value
                );
                return (
                  <div>
                    <span
                      className="mr-2 inline-block h-2.5 w-2.5 rounded-full"
                      style={{
                        backgroundColor: getServiceAdvisorColor(
                          serviceAdvisor?.id,
                          serviceAdvisors || []
                        ),
                      }}
                    ></span>
                    {serviceAdvisor?.firstName} {serviceAdvisor?.lastName}
                  </div>
                );
              }}
            />
          </div>
        )}
        {isTechnicianFilterEnabled && (
          <div className="grid grid-cols-2 items-center gap-1">
            <p className="text-sm text-primary">
              {terminolize(
                "Technician",
                TerminologyOptions.technician,
                "singular"
              )}
            </p>
            <div>
              <MultiSelect
                onChange={(value) => onTechnicianChange(value)}
                value={
                  typeof technicianId === "string"
                    ? [technicianId]
                    : (technicianId ?? [])
                }
                placeholder={"All"}
                options={
                  technicians
                    ?.filter((sa) => !sa.hideInShopHero)
                    .map((serviceAdvisor) => ({
                      value: serviceAdvisor.id,
                      label: `${serviceAdvisor.firstName} ${serviceAdvisor.lastName}`,
                    })) ?? []
                }
              />
            </div>
          </div>
        )}
        {onFilteringColorsChange && (
          <div className="grid grid-cols-2 items-center gap-1">
            <p className="text-sm text-primary">Card Color</p>
            <MultiSelect
              onChange={(value) => onFilteringColorsChange(value)}
              value={filteringColors ?? []}
              placeholder={"All"}
              options={
                colors?.map((color) => ({
                  value: color.color,
                  label: color.title,
                })) ?? []
              }
              renderItem={(option) => {
                return (
                  <div className="flex items-center gap-1">
                    <span
                      className="mr-2 inline-block h-4 w-4 rounded-sm px-2"
                      style={{
                        backgroundColor: option.value,
                      }}
                    ></span>
                    {option.label}
                  </div>
                );
              }}
            />
          </div>
        )}
        {onFilteringContainerChange && containers && containers.length > 0 && (
          <div className="grid grid-cols-2 items-center gap-1">
            <p className="text-sm text-primary">Container</p>
            <MultiSelect
              onChange={(value) => onFilteringContainerChange(value)}
              value={filteringContainer ?? []}
              placeholder={"All"}
              options={containers.map((container) => ({
                value: container.id,
                label: container.displayName || container.id,
              }))}
            />
          </div>
        )}
        <DueDateSelect
          startValue={dueInStart}
          endValue={dueInEnd}
          onStartChange={onDueInStartChange}
          onEndChange={onDueInEndChange}
          onPreselectClick={(range) => handleDefaultDueClick("in", range)}
          title={"Drop Off Time"}
        />
        <DueDateSelect
          startValue={dueOutStart}
          endValue={dueOutEnd}
          onStartChange={onDueOutStartChange}
          onEndChange={onDueOutEndChange}
          onPreselectClick={(range) => handleDefaultDueClick("out", range)}
          title={"Promise Time"}
        />
        <DueDateSelect
          startValue={apptStartStart}
          endValue={apptStartEnd}
          onStartChange={onApptStartStartChange}
          onEndChange={onApptStartEndChange}
          onPreselectClick={(range) =>
            handleDefaultDueClick("apptStart", range)
          }
          title={"Appointment Start"}
        />
        <DueDateSelect
          startValue={apptEndStart}
          endValue={apptEndEnd}
          onStartChange={onApptEndStartChange}
          onEndChange={onApptEndEndChange}
          onPreselectClick={(range) => handleDefaultDueClick("apptEnd", range)}
          title={"Appointment End"}
        />
        <div className="flex flex-col gap-1">
          <p className="text-sm text-primary">Tags</p>
          <Tags
            allTags={tags ?? []}
            tags={filteringTags ?? []}
            suggestedTags={tags
              ?.map((t) => t.label)
              .filter((t) => !(filteringTags ?? []).includes(t))}
            onTagCreate={handleTagAdd}
            onTagRemove={handleTagRemove}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};

const DueDateSelect = ({
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  onPreselectClick,
  title = "Due In",
}: {
  startValue: string | undefined | null;
  endValue: string | undefined | null;
  onStartChange?: (value: string | undefined) => void;
  onEndChange?: (value: string | undefined) => void;
  onPreselectClick: (range: Range) => void;
  title?: string;
}) => {
  const startDate = parseDateString(startValue);
  const endDate = parseDateString(endValue);

  // Simplified conditional checks
  const { isPast, isToday, isNextWeek, isThisWeek } = getMatchingDateRange(
    startDate,
    endDate
  );

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-row items-center justify-between gap-2">
        <p className="text-sm text-primary">{title}</p>
        <div className={"flex-grow"}></div>

        <Button
          size={"tinyPill"}
          onClick={() => onPreselectClick("past")}
          variant={isPast ? "accent" : "default"}
        >
          Past
        </Button>
        <Button
          size={"tinyPill"}
          onClick={() => onPreselectClick("today")}
          variant={isToday ? "accent" : "default"}
        >
          Today
        </Button>
        <Button
          size={"tinyPill"}
          onClick={() => onPreselectClick("week")}
          variant={isThisWeek ? "accent" : "default"}
        >
          This Week
        </Button>
        <Button
          size={"tinyPill"}
          onClick={() => onPreselectClick("nextWeek")}
          variant={isNextWeek ? "accent" : "default"}
        >
          Next Week
        </Button>
      </div>
      <div className="flex flex-row items-center justify-between gap-2">
        <DatePicker
          className={"w-full"}
          value={
            startValue ? parse(startValue, "yyyy-MM-dd", new Date()) : undefined
          }
          onChange={(value) =>
            onStartChange
              ? onStartChange(value ? format(value, "yyyy-MM-dd") : value)
              : undefined
          }
        />
        <p>to</p>
        <DatePicker
          className={"w-full"}
          value={
            endValue ? parse(endValue, "yyyy-MM-dd", new Date()) : undefined
          }
          onChange={(value) =>
            onEndChange
              ? onEndChange(value ? format(value, "yyyy-MM-dd") : value)
              : undefined
          }
        />
      </div>
    </div>
  );
};
